import React, { useContext, useRef, useState } from 'react';
import styles from './style.module.scss';
import { toast } from 'react-toastify';
import MultipleUserModal from './MultipleUserModal';
import useAPI from '../../../hooks/useAPI';
import AuthContext from '../../../contexts/AuthContext';
import DownloadIcon from '../../../assets/downloadIcon.png';

type MultipleUserProps = {
  onClose: () => void;
  organizations: string;
  setActiveTab?: (tab: 'single' | 'multiple') => void;
};

const MAX_FILE_SIZE_MB = 10;
const MAX_ROWS = 5000;
const REQUIRED_HEADERS = [
  'Role',
  'OrganizationName',
  'FirstName',
  'LastName',
  'Email',
  'EmployeeCode',
];

const MultipleUser: React.FC<MultipleUserProps> = ({
  onClose,
  organizations,
  setActiveTab = (tab: 'single' | 'multiple') => {},
}) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [csvRows, setCsvRows] = useState<any[]>([]);
  const { userData } = useContext(AuthContext);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploadDisabled, setIsUploadDisabled] = useState(false);

  const allowedRoles = ['EMPLOYEE'];

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setError('');

    if (!file) return;

    const isCSV = file.name.toLowerCase().endsWith('.csv');
    const isTooLarge = file.size > MAX_FILE_SIZE_MB * 1024 * 1024;

    if (!isCSV) {
      setError('Only .csv files are allowed.');
      setCsvFile(null);
      return;
    }

    if (isTooLarge) {
      setError('File size must be 10MB or less.');
      setCsvFile(null);
      return;
    }

    e.target.value = '';
    const text = await file.text();
    const lines = text
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean);

    if (lines.length < 2) {
      setError('CSV must have at least one row of data.');
      setCsvFile(null);
      return;
    }

    const rawHeaders = lines[0].split(',').map(header => header.trim());

    const hasAllHeaders = REQUIRED_HEADERS.every(req =>
      rawHeaders.includes(req),
    );

    if (!hasAllHeaders) {
      const missingHeaders = REQUIRED_HEADERS.filter(
        req => !rawHeaders.includes(req),
      );
      setError(
        `CSV is missing required header(s): ${missingHeaders.join(', ')}. Headers are case-sensitive. Please use the exact template.`,
      );
      setCsvFile(null);
      return;
    }

    const dataRows: any[] = [];
    let blankRowFound = false;
    let headerRowFound = false;

    const isEmpty = (row: any) => {
      return REQUIRED_HEADERS.every(h => {
        const value = row[h];
        return !value || value.trim() === '';
      });
    };

    lines
      .slice(1)
      .filter(line => {
        return line.split(',').some(allLine => {
          return allLine.trim() !== '';
        });
      })
      .forEach((row, rowIndex) => {
        const values = row.split(',');
        const obj: any = {};

        rawHeaders.forEach((h, i) => {
          obj[h] = values[i]?.trim().replace(/^"|"$/g, '');
        });

        const isEmptyRow = REQUIRED_HEADERS.some(h => {
          const value = obj[h];
          return !value || value.trim() === '';
        });

        if (isEmptyRow) {
          blankRowFound = true;
          return;
        }

        dataRows.push(obj);
      });

    if (blankRowFound) {
      setError(
        'CSV contains rows with missing required fields. Please fill all columns.',
      );
      setCsvFile(null);
      return;
    }

    const invalidRoles = dataRows.filter(
      row => row['Role']?.trim().toLowerCase() !== 'employee',
    );

    if (invalidRoles.length > 0) {
      setError(
        `Invalid role(s) found in CSV. Allowed role is: "employee" (case-insensitive).`,
      );
      setCsvFile(null);
      return;
    }

    const firstNameWithNumbers = dataRows.filter(row => {
      const firstName = row['FirstName'] || '';
      return /\d/.test(firstName);
    });

    if (firstNameWithNumbers.length > 0) {
      setError('First Name must not contain numbers.');
      setCsvFile(null);
      return;
    }

    // Validate LastName for numbers
    const lastNameWithNumbers = dataRows.filter(row => {
      const lastName = row['LastName'] || '';
      return /\d/.test(lastName);
    });

    if (lastNameWithNumbers.length > 0) {
      setError('Last Name must not contain numbers.');
      setCsvFile(null);
      return;
    }

    // Validate organization names
    // Utility: Convert to Title Case (First letter capital, rest lowercase)
    const toTitleCase = (str: string) =>
      str
        .trim()
        .split(/\s+/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

    // Validate organization names
    const allowedOrgLower = organizations.toLowerCase();

    const invalidOrganizations = dataRows.filter(row => {
      const orgName = (row['OrganizationName'] || '').toLowerCase();
      return orgName !== allowedOrgLower;
    });

    if (invalidOrganizations.length > 0) {
      setError(
        `Invalid organization(s) found in CSV. Allowed organization is: "${organizations}". It is case-insensitive.`,
      );
      setCsvFile(null);
      return;
    }

    const emailSet = new Set<string>();
    const empCodeSet = new Set<string>();
    let hasDuplicateEmail = false;
    let hasDuplicateCode = false;

    for (const row of dataRows) {
      const email = row['Email']?.trim().replace(/^"|"$/g, '').toLowerCase();

      const code = row['EmployeeCode']
        ?.trim()
        .replace(/^"|"$/g, '')
        .toLowerCase();

      if (emailSet.has(email)) {
        hasDuplicateEmail = true;
        break;
      }
      if (empCodeSet.has(code)) {
        hasDuplicateCode = true;
        break;
      }

      emailSet.add(email);
      empCodeSet.add(code);
    }

    if (hasDuplicateEmail) {
      setError('Duplicate email IDs found in CSV. Each email must be unique.');
      setCsvFile(null);
      return;
    }

    if (hasDuplicateCode) {
      setError(
        'Duplicate employee codes found in CSV. Each employee code must be unique.',
      );
      setCsvFile(null);
      return;
    }

    if (dataRows.length > MAX_ROWS) {
      setError('CSV exceeds maximum row limit (5000).');
      setCsvFile(null);
      return;
    }

    setCsvRows(dataRows);
    setCsvFile(file);
    setIsUploadDisabled(true);
    toast.success(`File "${file.name}" ready for upload.`);
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const resetUpload = () => {
    setCsvFile(null);
    setCsvRows([]);
    setShowPreview(false);
  };

  const { refetch: importUsers, isLoading: isImporting } = useAPI({
    url: `${process.env.REACT_APP_API_BASE_URL}/auth/user/import`,
    method: 'POST',
    isFormData: true,
    autoFetch: false,
    onSuccess: (res: any) => {
      const importedCount = res?.result?.count || 0;

      toast.success(
        ` ${importedCount} employee${importedCount !== 1 ? 's' : ''} added successfully!`,
      );

      resetUpload();

      onClose();
      if (typeof setActiveTab === 'function') {
        setActiveTab('single');
      }
    },
    onError: (err: any) => {
      const serverErrors = err?.response?.data?.errors;

      if (Array.isArray(serverErrors) && serverErrors.length > 0) {
        // Check if duplicate email/code errors are present
        const allMessages = serverErrors.flatMap((e: any) => e.errors);
        const hasEmailConflict = allMessages.some((msg: string) =>
          msg.toLowerCase().includes('email is already exists'),
        );
        const hasCodeConflict = allMessages.some((msg: string) =>
          msg.toLowerCase().includes('employee code is already exists'),
        );

        if (hasEmailConflict || hasCodeConflict) {
          let customMsg = 'Some ';
          if (hasEmailConflict) customMsg += 'emails';
          if (hasEmailConflict && hasCodeConflict) customMsg += ' and ';
          if (hasCodeConflict) customMsg += 'employee codes';
          customMsg += ' already exist.';
          toast.error(customMsg);
        } else {
          // Fallback for other server validation errors
          toast.error('Validation failed. Please check the CSV data.');
        }
      } else {
        // Generic error fallback
        toast.error(
          'Upload failed: ' + (err?.response?.data?.message || 'Unknown error'),
        );
      }

      // Reset tab to 'single'
      if (typeof setActiveTab === 'function') {
        setActiveTab('single');
      }
    },
  });

  const handleAddUsers = () => {
    if (!csvFile) {
      toast.error('No file selected.');
      return;
    }

    const formData = new FormData();
    formData.append('file', csvFile);

    toast.promise(importUsers(formData), {
      pending: 'Uploading users...',
    });
  };

  const handleDownloadTemplate = async (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    e.preventDefault();
    setIsDownloading(true);

    try {
      const link = document.createElement('a');
      link.href = '/template/demo-template.csv';
      link.download = 'demo-template.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      setTimeout(() => setIsDownloading(false), 1000);
    }
  };

  return (
    <div className={styles.bulkUploadContainer}>
      {!showPreview && (
        <>
          <p className={styles.infoText}>
            Quickly onboard multiple employees by uploading their details in a
            single CSV file using the provided template.
          </p>

          <span className={styles.uploadLabel}>
            Upload file with employee details
          </span>

          <div className={styles.uploadSection}>
            <label
              htmlFor="csvUpload"
              className={`${styles.uploadBox} ${
                isUploadDisabled ? styles.disabledUploadBox : ''
              }`}
            >
              <div className={styles.uploadIconContainer}>
                <span className={styles.uploadIcon}>
                  <img
                    src={DownloadIcon}
                    alt="upload icon"
                    width={100}
                    height={100}
                  />
                </span>
                <div className={styles.uploadTextContainer}>
                  <p className={styles.uploadText}>
                    Select a file or drag & drop here
                  </p>
                  <p className={styles.fileFormat}>
                    File Format: CSV (up to 10MB)
                  </p>
                </div>
                <button
                  className={
                    isUploadDisabled
                      ? styles.uploadBtnDisabled
                      : styles.uploadBtn
                  }
                  type="button"
                  onClick={handleUpload}
                  disabled={isUploadDisabled}
                >
                  Upload
                </button>
              </div>
              <input
                id="csvUpload"
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className={styles.hiddenInput}
                onChange={handleFileChange}
              />
            </label>
          </div>

          {error && <p className={styles.errorText}>{error}</p>}

          {csvFile && (
            <div className={styles.fileDetailsContainer}>
              <div className={styles.uploadProgress}>
                <div className={styles.uploadedFile}>
                  <div className={styles.fileInfo}>
                    <span className={styles.fileIcon}>📄</span>
                    <span className={styles.fileName}>{csvFile.name}</span>
                    <span className={styles.fileSize}>
                      {(csvFile.size / 1024).toFixed(2)} KB
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.deleteBtn}
                    onClick={() => {
                      setCsvFile(null);
                      setIsUploadDisabled(false);
                    }}
                  >
                    x
                  </button>
                </div>
              </div>
              <div className={styles.proceedContainer}>
                <button
                  className={styles.proceedBtn}
                  type="button"
                  onClick={() => setShowPreview(true)}
                >
                  Proceed
                </button>
              </div>
            </div>
          )}
          <div className={styles.templateDownload}>
            <a
              href="/template/demo-template.csv"
              className={styles.downloadTemplate}
              onClick={handleDownloadTemplate}
            >
              {isDownloading ? (
                <span className={styles.spinner}></span>
              ) : (
                'Download Template'
              )}
            </a>
          </div>
        </>
      )}

      {showPreview && (
        <MultipleUserModal
          data={csvRows}
          show={showPreview}
          onClose={() => {
            setShowPreview(false);
            onClose();
          }}
          onCancel={() => setShowPreview(false)}
          onAdd={handleAddUsers}
        />
      )}
    </div>
  );
};

export default MultipleUser;
